import { Separator } from "@/components/ui/separator";
import { useFormFieldVisibility } from "@/context/FormFieldVisibilityContext";
import {
  collapseMaskableSeparators,
  MASKABLE_SEPARATOR_ATTR,
  resetMaskableSeparators,
} from "@/lib/maskable-separators";
import {
  useLayoutEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from "react";

type MaskableSeparatorProps = ComponentPropsWithoutRef<typeof Separator>;

/**
 * A plain field-divider separator that disappears when it no longer sits
 * between two visible field groups. Drop-in replacement for <Separator/> inside
 * maskable forms; outside a masking-enabled form it behaves like a normal one.
 *
 * The hide/show decision is made by a shared collapse pass over the separator's
 * siblings (see `collapseMaskableSeparators`), re-run whenever field visibility
 * changes, so leading, trailing and redundant separators all vanish logically —
 * not only on "Tout masquer".
 */
export function MaskableSeparator({
  className,
  ...props
}: MaskableSeparatorProps) {
  const { enabled, visibility, registryVersion } = useFormFieldVisibility();
  const ref = useRef<ElementRef<typeof Separator>>(null);

  useLayoutEffect(() => {
    const parent = ref.current?.parentElement ?? null;
    if (enabled) collapseMaskableSeparators(parent);
    else resetMaskableSeparators(parent);
    // Re-evaluate on every visibility/registry change (a field mounting,
    // unmounting or toggling can empty or fill an adjacent group).
  }, [enabled, visibility, registryVersion]);

  return (
    <Separator
      ref={ref}
      {...{ [MASKABLE_SEPARATOR_ATTR]: "" }}
      className={className}
      {...props}
    />
  );
}
